import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import {
  useGetLastDeploymentWorkflowRun,
  useListAvailableReleases,
  usePromoteReleaseMulti,
} from '@sunstone/api-client/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { errorToastMessage } from '@/lib/errors';

interface PromoteWorkspacesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 1–5 workspaces, already validated by the caller (getBulkPromoteEligibility)
   * to share deploymentId/currentReleaseId. */
  workspaces: Workspace[];
  /** Called after a successful dispatch — caller clears its selection here. */
  onSuccess: () => void;
}

function pluralize(n: number): string {
  return n === 1 ? 'workspace' : 'workspaces';
}

/**
 * Confirm-and-dispatch dialog for promoting/upgrading up to 5 workspaces at
 * once via PromoteReleaseMulti. Offers exactly two fixed targets — no
 * combobox — mirroring the single-workspace flow's own two entry points
 * (WorkspacePage's "Promote Workspace" vs WorkspaceDetail's "Preview Upgrade
 * to X"): promote (refresh to the shared current release) or upgrade to the
 * immediate-next available release, when one exists. Reused from both the
 * global Workspaces table and a deployment's workspace card grid.
 */
export function PromoteWorkspacesDialog({
  open,
  onOpenChange,
  workspaces,
  onSuccess,
}: PromoteWorkspacesDialogProps) {
  const { token } = useAuthToken();
  const { hasPermission } = useAuthorization();
  const navigate = useNavigate();
  const params = useParams<{ deploymentId?: string }>();
  const anchor = workspaces[0];
  const n = workspaces.length;
  // The dialog is shared between the global Workspaces page (no deploymentId
  // route param) and a deployment's own workspace grid (route param set) -
  // only toast a "View deployment" link when the caller can't already see
  // the deployment's own banner (WorkflowRunBanner) on the current page.
  const onDeploymentPage = !!anchor && params.deploymentId === anchor.deploymentId;
  const deploymentToastAction = onDeploymentPage
    ? undefined
    : {
        label: 'View deployment',
        onClick: () => navigate(`/tenants/${anchor?.tenantId}/deployments/${anchor?.deploymentId}`),
      };

  // Pre-flight guard: the same data the deployment's own banner reads. Catches
  // "already running" before dispatching at all - Temporal's deterministic
  // workflow ID only blocks a collision while the prior execution is still
  // open, so a stale in_progress row here wouldn't otherwise stop a second
  // dispatch from actually starting (see the dispatchedAtMs===0n backstop
  // below for that narrower race).
  const { data: deploymentRunData } = useGetLastDeploymentWorkflowRun(
    open ? (anchor?.deploymentId ?? '') : '',
    token,
  );
  const deploymentRunInProgress =
    deploymentRunData?.found && deploymentRunData.status === 'in_progress';

  const { data: availableReleasesData, isLoading: isLoadingReleases } = useListAvailableReleases(
    open ? (anchor?.id ?? '') : '',
    token,
    'baic',
  );
  // availableReleases is sorted newest-first by the backend, so the last
  // entry is the smallest version still greater than current — i.e. the
  // immediate next release, matching WorkspaceDetail's own derivation.
  const availableReleases = availableReleasesData?.releases ?? [];
  const nextRelease = availableReleases[availableReleases.length - 1];

  const [action, setAction] = useState<'promote' | 'upgrade'>('promote');
  const [runLiquibase, setRunLiquibase] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);

  useEffect(() => {
    if (open) {
      setAction('promote');
      setRunLiquibase(true);
    }
  }, [open]);

  const promoteMulti = usePromoteReleaseMulti(token);
  const planMulti = usePromoteReleaseMulti(token);

  const releaseId = action === 'upgrade' ? nextRelease?.releaseId : anchor?.currentReleaseId;
  const permission = action === 'upgrade' ? 'upgrade:workspace' : 'promote:workspace';
  const label =
    action === 'upgrade'
      ? `Upgrade ${n} ${pluralize(n)} to v${nextRelease?.version}`
      : `Promote ${n} ${pluralize(n)}`;

  function handlePlan() {
    if (!releaseId) return;
    if (deploymentRunInProgress) {
      toast.info('A promote/upgrade is already running for this deployment.');
      return;
    }
    planMulti.mutate(
      {
        workspaceIds: workspaces.map((w) => w.id),
        releaseId,
        runLiquibase,
        terraformOperation: 'plan',
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          if (data.dispatchedAtMs === 0n) {
            // Temporal's deterministic workflow ID collided with an
            // already-running batch for this deployment (plan and apply
            // share the same ID) — swallowed as a no-op success server-side,
            // so nothing new was actually dispatched.
            toast.info('A promote/upgrade is already running for this deployment.');
          } else {
            toast.success(
              action === 'upgrade'
                ? `Plan started for ${n} ${pluralize(n)} to v${nextRelease?.version}.`
                : `Plan started for ${n} ${pluralize(n)}.`,
              { action: deploymentToastAction },
            );
          }
          onSuccess();
        },
        onError: (err) => toast.error(errorToastMessage(err, 'Failed to start the release plan.')),
      },
    );
  }

  function handleConfirm() {
    if (!releaseId) return;
    if (deploymentRunInProgress) {
      toast.info('A promote/upgrade is already running for this deployment.');
      return;
    }
    promoteMulti.mutate(
      { workspaceIds: workspaces.map((w) => w.id), releaseId, runLiquibase },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          if (data.dispatchedAtMs === 0n) {
            toast.info('A promote/upgrade is already running for this deployment.');
          } else {
            toast.success(
              action === 'upgrade'
                ? `Upgrade started for ${n} ${pluralize(n)} to v${nextRelease?.version}.`
                : `Promote started for ${n} ${pluralize(n)}.`,
              { action: deploymentToastAction },
            );
          }
          onSuccess();
        },
        onError: (err) =>
          toast.error(errorToastMessage(err, 'Failed to start the release promote.')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Promote / Upgrade Workspaces</DialogTitle>
          <DialogDescription>
            {n} {pluralize(n)} selected, all on v{anchor?.currentVersion || 'unknown'}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ul className="text-sm text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
            {workspaces.map((w) => (
              <li key={w.id} className="font-mono truncate">
                {w.displayName}
              </li>
            ))}
          </ul>

          {isLoadingReleases ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading available releases…
            </div>
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setAction('promote')}
                className={`w-full text-left rounded-md border p-3 text-sm transition-colors ${
                  action === 'promote' ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <p className="font-medium text-foreground">
                  Promote current release (v{anchor?.currentVersion || 'unknown'})
                </p>
                <p className="text-xs text-muted-foreground">
                  Re-applies the currently running release to every selected workspace.
                </p>
              </button>
              {nextRelease && (
                <button
                  type="button"
                  onClick={() => setAction('upgrade')}
                  className={`w-full text-left rounded-md border p-3 text-sm transition-colors ${
                    action === 'upgrade' ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <p className="font-medium text-foreground">Upgrade to v{nextRelease.version}</p>
                  <p className="text-xs text-muted-foreground">
                    Moves every selected workspace to the next available release.
                  </p>
                </button>
              )}
            </div>
          )}

          <div className="flex items-start gap-2.5">
            <Checkbox
              id="promote-multi-run-liquibase"
              checked={runLiquibase}
              onCheckedChange={(v) => setRunLiquibase(v === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="promote-multi-run-liquibase"
              className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
            >
              Run Liquibase migrations
            </Label>
          </div>

          {(planMulti.error || promoteMulti.error) && (
            <p className="text-sm text-destructive">
              {errorToastMessage(
                planMulti.error ?? promoteMulti.error,
                'Failed to start the release promote.',
              )}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={
              !hasPermission(permission) || !releaseId || isLoadingReleases || planMulti.isPending
            }
            onClick={handlePlan}
          >
            {planMulti.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Planning…
              </>
            ) : (
              'Plan'
            )}
          </Button>
          <Button
            disabled={
              !hasPermission(permission) ||
              !releaseId ||
              isLoadingReleases ||
              promoteMulti.isPending
            }
            onClick={() => setConfirmOpen(true)}
          >
            {promoteMulti.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {action === 'upgrade' ? 'Upgrading…' : 'Promoting…'}
              </>
            ) : (
              label
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(next) => {
          setConfirmOpen(next);
          if (!next) setUnderstood(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{label}</AlertDialogTitle>
            <AlertDialogDescription>
              This will restart services in {n} {pluralize(n)} and may cause a brief interruption.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2.5">
            <Checkbox
              id="promote-multi-understood"
              checked={understood}
              onCheckedChange={(v) => setUnderstood(v === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="promote-multi-understood"
              className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
            >
              I understand and want to continue.
            </Label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!understood}
              onClick={() => {
                setConfirmOpen(false);
                setUnderstood(false);
                handleConfirm();
              }}
            >
              {label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
